
import { apiGet, apiPost, apiPostWithParams, validateRequiredParams } from '@/services/BaseApiService';
import { RegisteredOrganization } from '../models/RegisteredOrganization';

const resourceUrl = '/program';

/**
 * Approves an organization.
 * @param {string} organizationId - The ID of the organization to approve.
 * @param {string} primaryContactId - The ID of the primary contact for the organization.
 * @returns {Promise<Object>} The response data after approving the organization.
 * @throws {Error} If any of the required parameters are missing or if there is an error approving the organization.
 */
export const approveOrganization = async (organizationId, primaryContactId) => {
    validateRequiredParams({ organizationId, primaryContactId });
    return await apiPostWithParams(`${resourceUrl}/approveorganization`, null, { id: organizationId, contactId: primaryContactId });
  };

  
  /**
 * Rejects an organization.
 * @param {string} organizationId - The ID of the organization to reject.
 * @returns {Promise<Object>} The response data after rejecting the organization.
 * @throws {Error} If any of the required parameters are missing or if there is an error rejecting the organization.
 */
  export const rejectOrganization = async (organizationId) => {
    validateRequiredParams({ organizationId });
    return await apiPostWithParams(`${resourceUrl}/rejectorganization`, null, { id: organizationId });
  };
  
  /**
 * Rejects a contact.
 * @param {string} contactId - The ID of the contact to reject.
 * @returns {Promise<Object>} The response data after rejecting the contact.
 * @throws {Error} If any of the required parameters are missing or if there is an error rejecting the contact.
 */
  export const rejectContact = async (contactId) => {
    validateRequiredParams({ contactId });
    return await apiPostWithParams(`${resourceUrl}/rejectcontact`, null, { id: contactId });
  };


  /**
 * Retrieves organization approvals.
 * @returns {Promise<Object>} The response data containing organization approvals.
 * @throws {Error} If there is an error retrieving organization approvals.
 */
  export const getOrganizationApprovals = async () => {
    return await apiGet(`${resourceUrl}/getorganizationapprovals`);
  };

  /**
 * Retrieves organizations.
 * @param {boolean} activeOnly - Whether to retrieve only active organizations.
 * @returns {Promise<Array<RegisteredOrganization>>} The response data containing organizations.
 * @throws {Error} If there is an error retrieving organizations.
 */
  export const getOrganizations = async (activeOnly) => {
    validateRequiredParams({ activeOnly });
    const response =  await apiGet(`${resourceUrl}/getorganizations`, { activeOnly });
    if (Array.isArray(response)) {
        return response.map(item => new RegisteredOrganization(item));
    } else {
      throw new Error('Invalid response data format');
    }
  };

  /**
 * Retrieves notes for an organization.
 * @param {string} organizationId - The ID of the organization.
 * @returns {Promise<Object>} The response data containing organization notes.
 * @throws {Error} If any of the required parameters are missing or if there is an error retrieving organization notes.
 */
  export const getOrganizationNotes = async (organizationId) => {
    validateRequiredParams({ organizationId });
    return await apiGet(`${resourceUrl}/getorganizationnotes`, { id: organizationId });

  };

  /**
 * Retrieves available programs for an organization.
 * @param {string} organizationId - The ID of the organization.
 * @returns {Promise<Object>} The response data containing available organization programs.
 * @throws {Error} If any of the required parameters are missing or if there is an error retrieving available organization programs.
 */
  export const getAvailableOrganizationPrograms = async (organizationId) => {
    validateRequiredParams({ organizationId });
    return await apiGet(`${resourceUrl}/getavailableorganizationprograms`, { id: organizationId });
  };

  /**
 * Retrieves programs for an organization.
 * @param {string} organizationId - The ID of the organization.
 * @returns {Promise<Object>} The response data containing organization programs.
 * @throws {Error} If any of the required parameters are missing or if there is an error retrieving organization programs.
 */
  export const getOrganizationPrograms = async (organizationId) => {
    validateRequiredParams({ organizationId });
    return await apiGet(`${resourceUrl}/getorganizationprograms`, { id: organizationId });
  };

  /**
 * Adds a program to an organization.
 * @param {string} organizationId - The ID of the organization.
 * @param {string} programName - The name of the program to add.
 * @returns {Promise<Object>} The response data after adding the program.
 * @throws {Error} If any of the required parameters are missing or if there is an error adding the program.
 */
  export const addOrganizationProgram = async (organizationId, programName) => {
    validateRequiredParams({ organizationId, programName });
    return await apiPostWithParams(`${resourceUrl}/insertorganizationprogram`, null, { id: organizationId, programName });
  };

  /**
 * Deletes a program from an organization.
 * @param {string} organizationId - The ID of the organization.
 * @param {string} programName - The name of the program to delete.
 * @returns {Promise<Object>} The response data after deleting the program.
 * @throws {Error} If any of the required parameters are missing or if there is an error deleting the program.
 */
  export const deleteOrganizationProgram = async (organizationId, programName) => {
    validateRequiredParams({ organizationId, programName });
    return await apiPostWithParams(`${resourceUrl}/deleteorganizationprogram`, null, { id: organizationId, programName });
  };

  /**
 * Retrieves a specific note for an organization.
 * @param {string} organizationId - The ID of the organization.
 * @param {string} noteId - The ID of the note to retrieve.
 * @returns {Promise<Object>} The response data containing the organization note.
 * @throws {Error} If any of the required parameters are missing or if there is an error retrieving the organization note.
 */
  export const getOrganizationNote = async (organizationId, noteId) => {
    validateRequiredParams({ organizationId, noteId });
    return await apiGet(`${resourceUrl}/getorganizationnote`, { id: organizationId, noteId });
  };

  /**
 * Inserts a note for an organization.
 * @param {Object} note - The note to insert.
 * @returns {Promise<Object>} The response data after inserting the note.
 * @throws {Error} If any of the required parameters are missing or if there is an error inserting the note.
 */
  export const insertOrganizationNote = async (note) => {
    validateRequiredParams({ note });
    return await apiPost(`${resourceUrl}/insertorganizationnote`, note);
  };

  /**
 * Updates a note for an organization.
 * @param {Object} note - The note to update.
 * @returns {Promise<Object>} The response data after updating the note.
 * @throws {Error} If any of the required parameters are missing or if there is an error updating the note.
 */
  export const updateOrganizationNote = async (note) => {
    validateRequiredParams({ note });
    return await apiPost(`${resourceUrl}/updateorganizationnote`, note);
  };

  /**
 * Deletes a note for an organization.
 * @param {string} noteId - The ID of the note to delete.
 * @returns {Promise<Object>} The response data after deleting the note.
 * @throws {Error} If any of the required parameters are missing or if there is an error deleting the note.
 */
  export const deleteOrganizationNote = async (noteId) => {
    validateRequiredParams({ noteId });
    return await apiPostWithParams(`${resourceUrl}/deleteorganizationnote`, null, { id: noteId });
  };


  /**
   * Retrieves annual reports based on the provided status group, report type, and report year.
   *
   * @async
   * @function getAnnualReports
   * @param {string} statusGroup - The status group to filter the annual reports.
   * @param {string} reportType - The type of report to retrieve.
   * @param {string|number} reportYear - The year of the report.
   * @returns {Promise<Object>} The data from the API response containing the annual reports.
   * @throws Will throw an error if the API request fails.
   */
  export const getAnnualReports = async (statusGroup, reportType, reportYear) => {
    validateRequiredParams({ statusGroup, reportType, reportYear });
    return await apiGet(`${resourceUrl}/getannualreports`, { statusGroup, reportType, reportYear });
  };

  /**
 * Retrieves HP report status history for a given report ID.
 * @param {string} reportId - The ID of the HP report.
 * @returns {Promise<Object>} The response data containing HP report status history.
 * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
 */
export const getHpReportStatusHistory = async (reportId) => {
   validateRequiredParams({ reportId });
   return await apiGet(`${resourceUrl}/gethpreportstatushistory`, { id: reportId });
  };

  /**
 * Retrieves FOSC report status history for a given report ID.
 * @param {string} reportId - The ID of the FOSC report.
 * @returns {Promise<Object>} The response data containing FOSC report status history.
 * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
 */
export const getFoscReportStatusHistory = async (reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${resourceUrl}/getfoscreportstatushistory`, { id: reportId });
  };

  /**
 * Retrieves DI report status history for a given report ID.
 * @param {string} reportId - The ID of the DI report.
 * @returns {Promise<Object>} The response data containing DI report status history.
 * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
 */
export const getDiReportStatusHistory = async (reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${resourceUrl}/getdireportstatushistory`, { id: reportId });
  };

    /**
   * Retrieves CEX report status history for a given report ID.
   * @param {string} reportId - The ID of the CEX report.
   * @returns {Promise<Object>} The response data containing CEX report status history.
   * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
   */
  export const getCexReportStatusHistory = async (reportId) => {
    validateRequiredParams({ reportId });
    return await apiGet(`${resourceUrl}/getcexreportstatushistory`, { id: reportId });

  };

  export const getUroReportStatusHistory = async (reportId) => {
    validateRequiredParams({ reportId });
    return await apiGet(`${resourceUrl}/geturoreportstatushistory`, { id: reportId });
  };

    /**
   * Retrieves transaction status history for a given transaction ID.
   * @param {string} transactionId - The ID of the transaction.
   * @returns {Promise<Object>} The response data containing transaction status history.
   * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
   */
  export const getTransactionStatusHistory = async (transactionId) => {
    validateRequiredParams({ transactionId });
    return await apiGet(`${resourceUrl}/gettransactionstatushistory`, { id: transactionId });
  };

  /**
 * Retrieves program transactions for a given user ID.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} The response data containing program transactions.
 * @throws {Error} If the required parameter is missing or if there is an error retrieving the transactions.
 */
export const getProgramTransactions = async (userId) => {
    validateRequiredParams({ userId });
    return await apiGet(`${resourceUrl}/getprogramtransactions`, { id: userId });
  };

export const getReportYears = async () => {
  return await apiGet(`${resourceUrl}/getreportyears`);
};

export const getProgramDashboardReportCounts = async () => {
  return await apiGet(`${resourceUrl}/dashboard/reportcounts`);
};

export const getProgramDashboardOrganizationCounts = async () => {
  return await apiGet(`${resourceUrl}/dashboard/organizationcounts`);
};

export const getProgramDashboardReportStatusCounts = async (reportYear) => {
  return await apiGet(`${resourceUrl}/dashboard/reportstatuscounts`, { reportYear });
};

